/**
 * H5 域名：推广域=扫码落地+微信登录；主站=微信支付（可不同）。
 * 作者: zbq | 日期: 2026-10-28
 */
import ZqApi from '@/sheep/api/zq';

const PRIMARY_KEY = 'zq_primary_domain';
const PROMO_KEY = 'zq_promo_domain';
let cachedPrimary = '';
let cachedPromo = '';

function normalizeOrigin(raw) {
  if (!raw || typeof raw !== 'string') return '';
  let s = raw.trim();
  if (!s) return '';
  try {
    if (!/^https?:\/\//i.test(s)) {
      s = `https://${s}`;
    }
    return new URL(s).origin;
  } catch (e) {
    return '';
  }
}

export function getCachedPrimaryOrigin() {
  if (cachedPrimary) return cachedPrimary;
  try {
    cachedPrimary = normalizeOrigin(uni.getStorageSync(PRIMARY_KEY));
  } catch (e) {
    /* ignore */
  }
  return cachedPrimary;
}

export function getCachedPromoOrigin() {
  if (cachedPromo) return cachedPromo;
  try {
    cachedPromo = normalizeOrigin(uni.getStorageSync(PROMO_KEY));
  } catch (e) {
    /* ignore */
  }
  return cachedPromo || getCachedPrimaryOrigin();
}

export function setPrimaryOrigin(raw) {
  const origin = normalizeOrigin(raw);
  if (!origin) return;
  cachedPrimary = origin;
  try {
    uni.setStorageSync(PRIMARY_KEY, origin);
  } catch (e) {
    /* ignore */
  }
}

export function setPromoOrigin(raw) {
  const origin = normalizeOrigin(raw);
  if (!origin) return;
  cachedPromo = origin;
  try {
    uni.setStorageSync(PROMO_KEY, origin);
  } catch (e) {
    /* ignore */
  }
}

/** 启动或 OAuth/支付前拉取主站 + 推广域 */
export async function ensureSiteDomain() {
  // #ifdef H5
  try {
    const res = await ZqApi.getYumingList();
    const body = res?.data || res || {};
    const list = Array.isArray(body.dataList) ? body.dataList : [];
    const primaryRow = list.find((d) => d && Number(d.primaryFlag) === 1);
    const promoRow = list.find((d) => d && Number(d.promoFlag) === 1);
    const primary =
      body.primaryDomain ||
      (primaryRow && (primaryRow.domain || primaryRow.url)) ||
      '';
    const promo =
      body.promoDomain ||
      (promoRow && (promoRow.domain || promoRow.url)) ||
      primary;
    if (primary) setPrimaryOrigin(primary);
    if (promo) setPromoOrigin(promo);
    return getCachedPrimaryOrigin();
  } catch (e) {
    /* ignore */
  }
  // #endif
  return getCachedPrimaryOrigin();
}

/** 支付域 origin（= 后台 primaryFlag / primaryDomain） */
export function getCachedPayOrigin() {
  return getCachedPrimaryOrigin();
}

/**
 * 支付绝对根：读后台支付域（primaryDomain），不写死域名。
 * 返回带尾斜杠。
 */
export function resolveH5RootUrl() {
  // #ifdef H5
  const primary = getCachedPayOrigin();
  if (primary) {
    return primary.endsWith('/') ? primary : `${primary}/`;
  }
  if (typeof window !== 'undefined' && window.location?.origin) {
    const o = window.location.origin;
    return o.endsWith('/') ? o : `${o}/`;
  }
  // #endif
  return '';
}

/** 当前是否在推广域（相对主站） */
export function isOnPromoHost() {
  // #ifdef H5
  if (typeof window === 'undefined' || !window.location?.hostname) return false;
  const primary = getCachedPrimaryOrigin();
  if (!primary) return false;
  try {
    const payHost = new URL(primary).hostname;
    return window.location.hostname !== payHost;
  } catch (e) {
    return false;
  }
  // #endif
  return false;
}

/**
 * 跨域跳主站时写入 URL 的会话字段（支付域与当前页不同根域时，cookie/localStorage 不通）。
 */
function collectCrossDomainPayParams() {
  const params = {};
  try {
    const invite = uni.getStorageSync('wxinvite') || '';
    const fid = uni.getStorageSync('wxfid') || '';
    const tenantId = uni.getStorageSync('tenant-id') || '';
    const uid = uni.getStorageSync('id') || uni.getStorageSync('zq_token') || '';
    const openid =
      uni.getStorageSync('openid') ||
      uni.getStorageSync('openId') ||
      uni.getStorageSync('wx_openid') ||
      '';
    if (invite) params.invite = String(invite);
    if (fid) {
      params.fid = String(fid);
      params.wxfid = String(fid);
    }
    if (tenantId) params.tenantId = String(tenantId);
    if (uid) {
      params.uid = String(uid);
      params.zq_uid = String(uid);
    }
    if (openid) params.openid = String(openid);
    params.zq_xpay = '1';
  } catch (e) {
    /* ignore */
  }
  return params;
}

/** 把 params 写入 search，并尽量写入 hash 路由 query（uni H5 常用 #/path?id=） */
function mergeParamsIntoHref(href, params) {
  const u = new URL(href);
  Object.keys(params).forEach((k) => {
    const v = params[k];
    if (v != null && v !== '') u.searchParams.set(k, String(v));
  });
  const hash = u.hash || '';
  if (hash.indexOf('#/') === 0) {
    const raw = hash.slice(1);
    const qIdx = raw.indexOf('?');
    const path = qIdx >= 0 ? raw.slice(0, qIdx) : raw;
    const hp = new URLSearchParams(qIdx >= 0 ? raw.slice(qIdx + 1) : '');
    Object.keys(params).forEach((k) => {
      const v = params[k];
      if (v != null && v !== '') hp.set(k, String(v));
    });
    const qs = hp.toString();
    u.hash = qs ? `#${path}?${qs}` : `#${path}`;
  }
  return u.toString();
}

/**
 * 若当前在推广域，跳到主站同路径；带上 invite/uid/openid（跨域必带）。
 * 仅用于微信支付前。登录不再强制跳主站。
 * @returns {boolean} true=已发起跳转
 */
export function redirectToPayHostIfNeeded() {
  // #ifdef H5
  const primary = getCachedPrimaryOrigin();
  if (!primary || typeof window === 'undefined') return false;
  try {
    const pay = new URL(primary);
    if (window.location.hostname === pay.hostname) return false;
    const base = `${pay.origin}${window.location.pathname}${window.location.search}${window.location.hash}`;
    const next = mergeParamsIntoHref(base, collectCrossDomainPayParams());
    window.location.replace(next);
    return true;
  } catch (e) {
    return false;
  }
  // #endif
  return false;
}
